/**
 * Raw palette scales — not used directly in feature code.
 * Semantic tokens reference these for light theme; dark mode can swap mappings later.
 */

export const slate = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

/** Brand blue accent — centered on #1c5aa5 */
export const accent = {
  50: '#edf3fc',
  100: '#d6e4f7',
  200: '#b0ccf0',
  300: '#7aade4',
  400: '#4a8dd4',
  500: '#2670bd',
  600: '#1f5f96',
  700: '#1c5aa5',
  800: '#174d8e',
  900: '#0f3d66',
};

/** RD-DASH-011: the ONE brand data ramp (charts, bars, donuts, capsules) + the dashboard card edge/hairlines. */
export const dataRamp = {
  900: '#1c5aa5',
  600: '#4f84c9',
  400: '#93b4e0',
  200: '#cddcf0',
  track: '#eef2f6',
};

/** RD-DASH-011: dashboard card edge, row hairline and secondary-button outline. */
export const homeEdge = {
  card: '#e6ebf1',
  hairline: '#eef2f6',
  button: '#dfe5ec',
};

export const green = {
  50: '#ecfdf5',
  100: '#d1fae5',
  200: '#a7f3d0',
  600: '#059669',
  700: '#047857',
};

export const red = {
  50: '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  600: '#dc2626',
  700: '#b91c1c',
};

export const amber = {
  50: '#fffbeb',
  100: '#fef3c7',
  200: '#fde68a',
  600: '#d97706',
  700: '#b45309',
  800: '#92400e',
};

export const blue = {
  50: '#eff6ff',
  100: '#dbeafe',
  600: '#2563eb',
  700: '#1c5aa5',
};

export const violet = {
  50: '#f5f3ff',
  100: '#ede9fe',
  600: '#7c3aed',
  700: '#6d28d9',
};

/**
 * Teal scale — used by the `operations` role tint (office dispatcher).
 * Distinct from admin-blue, supervisor-violet, billing-amber, worker-green.
 */
export const teal = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  700: '#0f766e',
};

/** Role-identity hues (RD-UI-028) — each one passes AA as 12px text on white. */
export const indigo = { 50: '#eef2ff', 200: '#c7d2fe', 700: '#4338ca' };
export const pink = { 50: '#fdf2f8', 200: '#fbcfe8', 700: '#be185d' };
export const sky = { 50: '#f0f9ff', 200: '#bae6fd', 700: '#0369a1' };
/** One deep navy for EVERY platform (CrewVex staff) role. */
export const platformNavy = { 50: '#eef3f9', 200: '#c3d3e6', 700: '#1e3a5f' };

/**
 * AI feature affordances — brand `accent` (job AI summary, route follow-up, platform AI pill, modals).
 * Distinct from status greens/ambers; pool industry pills use flat `accent[700]` — AI uses gradient + Sparkles.
 */
export const aiFeature = {
  gradient: `linear-gradient(148deg, ${accent[800]} 0%, ${accent[700]} 48%, ${accent[500]} 100%)`,
  border: accent[900],
  borderSoft: 'rgba(28, 90, 165, 0.2)',
  surfaceTint: 'rgba(28, 90, 165, 0.06)',
  gradientStart: accent[800],
  gradientMid: accent[700],
  gradientEnd: accent[500],
  borderGradient: `linear-gradient(135deg, ${accent[900]}, ${accent[500]})`,
  shadow: '0 1px 2px rgba(15, 61, 102, 0.18), 0 3px 10px rgba(28, 90, 165, 0.12)',
  shadowHover: '0 2px 4px rgba(15, 61, 102, 0.2), 0 8px 20px rgba(28, 90, 165, 0.14)',
  icon: accent[700],
  buttonGradient: `linear-gradient(148deg, ${accent[800]}, ${accent[700]})`,
  buttonBorder: accent[900],
  disabledBg: 'rgba(28, 90, 165, 0.08)',
  disabledText: accent[800],
  disabledBorder: accent[200],
  focusRing: 'rgba(74, 141, 212, 0.5)',
};
